import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BreathworkExercise {
  id?: string;
  title: string;
  description: string;
  instructions: string;
  breath_pattern: {
    inhale: number;
    hold: number;
    exhale: number;
    pause: number;
  };
  duration_minutes: number;
  difficulty_level: string;
  benefits: string[];
  category: string;
  is_published: boolean;
  sort_order: number;
}

export default function BreathworkManager() {
  const [exercises, setExercises] = useState<BreathworkExercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingExercise, setEditingExercise] = useState<BreathworkExercise | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const defaultExercise: BreathworkExercise = {
    title: '',
    description: '',
    instructions: '',
    breath_pattern: {
      inhale: 4,
      hold: 4,
      exhale: 4,
      pause: 4
    },
    duration_minutes: 5,
    difficulty_level: 'beginner',
    benefits: [],
    category: 'general',
    is_published: true,
    sort_order: 0
  };

  useEffect(() => {
    loadExercises();
  }, []);

  const loadExercises = async () => {
    try {
      const { data, error } = await supabase
        .from('breathwork_exercises')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      // Cast the data to proper types since breath_pattern comes as Json
      const typedData = (data || []).map(item => ({
        ...item,
        breath_pattern: item.breath_pattern as BreathworkExercise['breath_pattern']
      }));
      setExercises(typedData);
    } catch (error) {
      console.error('Error loading exercises:', error);
      toast.error('Failed to load breathwork exercises');
    } finally {
      setLoading(false);
    }
  };

  const saveExercise = async (exercise: BreathworkExercise) => {
    try {
      if (exercise.id) {
        const { error } = await supabase
          .from('breathwork_exercises')
          .update(exercise)
          .eq('id', exercise.id);
        if (error) throw error;
        toast.success('Exercise updated successfully');
      } else {
        const { error } = await supabase
          .from('breathwork_exercises')
          .insert([exercise]);
        if (error) throw error;
        toast.success('Exercise created successfully');
      }
      
      loadExercises();
      setEditingExercise(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving exercise:', error);
      toast.error('Failed to save exercise');
    }
  };

  const deleteExercise = async (id: string) => {
    try {
      const { error } = await supabase
        .from('breathwork_exercises')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Exercise deleted successfully');
      loadExercises();
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast.error('Failed to delete exercise');
    }
  };

  const ExerciseForm = ({ exercise, onSave, onCancel }: { 
    exercise: BreathworkExercise; 
    onSave: (exercise: BreathworkExercise) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(exercise);

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{exercise.id ? 'Edit Breathwork Exercise' : 'Create New Breathwork Exercise'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="4-7-8 Breathing"
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="A calming breathing technique for relaxation"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="instructions">Instructions</Label>
            <Textarea
              id="instructions"
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              placeholder="Step-by-step instructions for this breathing exercise"
              rows={4}
            />
          </div>
          
          <div>
            <Label>Breath Pattern (seconds)</Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              <div>
                <Label htmlFor="inhale" className="text-xs">Inhale</Label>
                <Input
                  id="inhale"
                  type="number"
                  value={formData.breath_pattern.inhale}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    breath_pattern: { 
                      ...formData.breath_pattern, 
                      inhale: parseInt(e.target.value) || 0 
                    }
                  })}
                />
              </div>
              <div>
                <Label htmlFor="hold" className="text-xs">Hold</Label>
                <Input
                  id="hold"
                  type="number"
                  value={formData.breath_pattern.hold}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    breath_pattern: { 
                      ...formData.breath_pattern, 
                      hold: parseInt(e.target.value) || 0 
                    }
                  })}
                />
              </div>
              <div>
                <Label htmlFor="exhale" className="text-xs">Exhale</Label>
                <Input
                  id="exhale"
                  type="number"
                  value={formData.breath_pattern.exhale}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    breath_pattern: { 
                      ...formData.breath_pattern, 
                      exhale: parseInt(e.target.value) || 0 
                    }
                  })}
                />
              </div>
              <div>
                <Label htmlFor="pause" className="text-xs">Pause</Label>
                <Input
                  id="pause"
                  type="number"
                  value={formData.breath_pattern.pause}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    breath_pattern: { 
                      ...formData.breath_pattern, 
                      pause: parseInt(e.target.value) || 0 
                    }
                  })}
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="duration_minutes">Duration (minutes)</Label>
              <Input
                id="duration_minutes"
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 5 })}
              />
            </div>
            
            <div>
              <Label htmlFor="difficulty_level">Difficulty</Label>
              <Select value={formData.difficulty_level} onValueChange={(value) => setFormData({ ...formData, difficulty_level: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="relaxation">Relaxation</SelectItem>
                  <SelectItem value="energizing">Energizing</SelectItem>
                  <SelectItem value="sleep">Sleep</SelectItem>
                  <SelectItem value="anxiety">Anxiety Relief</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="benefits">Benefits (comma separated)</Label>
            <Input
              id="benefits"
              value={formData.benefits.join(', ')}
              onChange={(e) => setFormData({ 
                ...formData, 
                benefits: e.target.value.split(',').map(b => b.trim()).filter(b => b) 
              })}
              placeholder="reduces anxiety, improves focus, calms mind"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
            
            <div className="flex items-center space-x-2 pt-6">
              <Switch
                id="is_published"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
              <Label htmlFor="is_published">Published</Label>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading breathwork exercises...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Breathwork Exercises Management</h2>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Exercise
        </Button>
      </div>
      
      {isCreating && (
        <ExerciseForm
          exercise={defaultExercise}
          onSave={saveExercise}
          onCancel={() => setIsCreating(false)}
        />
      )}
      
      {editingExercise && (
        <ExerciseForm
          exercise={editingExercise}
          onSave={saveExercise}
          onCancel={() => setEditingExercise(null)}
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {exercises.map((exercise) => (
          <Card key={exercise.id} className={!exercise.is_published ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{exercise.title}</CardTitle>
                  <div className="flex gap-1 mt-1">
                    <Badge variant="secondary">{exercise.category}</Badge>
                    <Badge variant="outline">{exercise.difficulty_level}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingExercise(exercise)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => exercise.id && deleteExercise(exercise.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{exercise.description}</p>
              
              <div className="space-y-2">
                <div className="text-sm">
                  <strong>Pattern:</strong> {exercise.breath_pattern.inhale}-{exercise.breath_pattern.hold}-{exercise.breath_pattern.exhale}-{exercise.breath_pattern.pause}
                </div>
                
                <div className="text-sm">
                  <strong>Duration:</strong> {exercise.duration_minutes} minutes
                </div>
                
                {exercise.benefits.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {exercise.benefits.map((benefit, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {benefit}
                      </Badge>
                    ))}
                  </div>
                )}
                
                <div className="text-xs text-muted-foreground">
                  Sort: {exercise.sort_order} | {exercise.is_published ? 'Published' : 'Draft'}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}